import { Divider } from 'src/design/atoms/Divider';
import { Grid } from 'src/design/atoms/Grid';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Input } from 'src/design/atoms/Input';
import { IconFileTypePdf } from '@tabler/icons-react';
import { isEqual } from 'lodash';
import React from 'react';
import { OrganizationOfficeDto } from 'src/api/FiretrolApiClient';
import {
  IAuditPdfRequest,
  useTimesheetAuditPdf,
} from 'src/api/queries/timesheets/useTimesheetReports';
import { OfficeSelector } from 'src/app/selectors/OfficeSelectorSingle';
import { TimesheetUserSelect } from 'src/routes/Reports/TimesheetAuditReport/TimesheetUserSelect';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Header } from 'src/design/organisms/Header';
import { PdfViewer } from 'src/design/organisms/PdfViewer/PdfViewer';
import { usePdfFile } from 'src/design/molecules/usePdfFile';
import { IDateOption } from 'src/routes/Jobs/Timesheets/types';
import { AuditWeekSelector, getCurrentWeekOption } from './AuditWeekSelector';

export const TimesheetAuditReport: React.FC = () => {
  const [office, setOffice] = React.useState<OrganizationOfficeDto | null>(
    null,
  );
  const [startWeek, setStartWeek] = React.useState<IDateOption | null>(
    getCurrentWeekOption(),
  );
  const [endWeek, setEndWeek] = React.useState<IDateOption | null>(
    getCurrentWeekOption(),
  );
  const [selectedUserId, setSelectedUserId] = React.useState<number | null>(
    null,
  );
  const [showOnlyMyTeam, setShowOnlyMyTeam] = React.useState(false);
  // Each server render is expensive, so the PDF follows the filters only when
  // Generate Report is pressed, not on every filter change.
  const [submitted, setSubmitted] = React.useState<IAuditPdfRequest | null>(
    null,
  );

  const request: IAuditPdfRequest | null =
    office && selectedUserId && startWeek && endWeek
      ? {
          officeId: office.id,
          userId: selectedUserId,
          startDate: startWeek.startDate,
          endDate: endWeek.endDate,
        }
      : null;

  const report = useTimesheetAuditPdf(submitted);
  const pdf = usePdfFile(report.data);
  const loading = report.isFetching;

  const generateReport = () => {
    if (!request) return;
    if (isEqual(request, submitted)) void report.refetch();
    else setSubmitted(request);
  };

  let emptyText = 'No report to display.';
  if (!submitted) {
    emptyText =
      'Choose an office, employee and week range, then select Generate Report.';
  } else if (report.isError) {
    emptyText = 'The report could not be generated.';
  }

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [
            { text: 'Reports', href: '/reports' },
            { text: 'Timesheet Audit Report' },
          ],
        }}
        title="Timesheet Audit Report"
        helpText="Generate detailed individual timesheet audit reports with history and signatures."
      />
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 3, lg: 2 }}>
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Title order={5}>Generate</Title>
              <Input.Wrapper label="Office">
                <OfficeSelector
                  onChange={() => {}}
                  onOfficeChange={(o) => {
                    setOffice(o);
                    setSelectedUserId(null);
                  }}
                  initialOfficeId={office?.id}
                  initialValue={''}
                />
              </Input.Wrapper>
              <Checkbox
                label="Show Only My Team"
                description="Only show employees that are supervised by you"
                checked={showOnlyMyTeam}
                onChange={(e) => {
                  setShowOnlyMyTeam(e.currentTarget.checked);
                  // Clear selected user if filtering changes
                  setSelectedUserId(null);
                }}
              />
              <Input.Wrapper label="Employee" withAsterisk>
                <TimesheetUserSelect
                  officeId={office?.id}
                  onlyMyTeam={showOnlyMyTeam}
                  value={selectedUserId}
                  onChange={setSelectedUserId}
                />
              </Input.Wrapper>
              <Input.Wrapper label="Start Week" withAsterisk>
                <AuditWeekSelector
                  value={startWeek}
                  onChange={(v) => setStartWeek(v)}
                />
              </Input.Wrapper>
              <Input.Wrapper label="End Week" withAsterisk>
                <AuditWeekSelector
                  value={endWeek}
                  onChange={(v) => setEndWeek(v)}
                />
              </Input.Wrapper>
              <BasicButton
                disabled={!request || loading}
                onClick={generateReport}
                loading={loading}
                fullWidth
              >
                Generate Report
              </BasicButton>
              <Divider />
              <Title order={5}>Download</Title>
              <BasicButton
                onClick={pdf.download}
                disabled={!pdf.url || loading}
                fullWidth
                variant="light"
                leftSection={<IconFileTypePdf size={18} />}
              >
                Download PDF
              </BasicButton>
            </Stack>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9, lg: 10 }}>
          <PdfViewer
            src={pdf.url}
            title="Timesheet Audit Report PDF"
            loading={loading}
            emptyText={emptyText}
          />
        </Grid.Col>
      </Grid>
    </>
  );
};
